/*
 * Auro: shared fixes for the step bundle builders (.ab). Loaded from snippets/auro-bundle-memory.liquid,
 * which every builder page renders. Works alongside each builder's own script without changing it.
 *
 * 1. Stock limits: cards carry data-max (stock, only when Shopify tracks it and overselling is off).
 *    + and Add stop at that number and the card says "Only N in stock".
 * 2. "Show all" opens 24 more cards at a time on long lists, then the builder's own expand takes over.
 * 3. "Show fewer" scrolls back to the top of the step, so shoppers are not left in the footer.
 *    The button also gets aria-expanded.
 * 4. Christmas builders: the free delivery line counts what is already in the basket, not only the bundle.
 * 5. Gift box builders (gift box, Diwali): a "Put this in a gift box" link from a product page now opens on the
 *    first step with the item already in the gift, instead of the item's own (often last) step.
 * 6. Gift box builders: after a filter or search is cleared, an item picked from further down the list is brought
 *    back into view (opening the list only as far as the shopper had already opened it).
 * 7. "Show all" is hidden on steps where every item already fits on screen (9 to 12 items on desktop).
 */
(function () {
  if (window.__auroBundleFixes) return;
  window.__auroBundleFixes = true;

  var BATCH = 24;
  var money = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' });

  function css() {
    var s = document.createElement('style');
    s.textContent =
      '.ab .ab-grid:not(.is-expanded) .ab-card.abf-shown:not([hidden]){display:flex}' +
      '.abf-stock{display:block;width:100%;margin-top:.6rem;font-size:1.2rem;line-height:1.3;text-align:center;color:#8a4b2a}' +
      '.ab-card [data-plus][aria-disabled="true"]{opacity:.35;cursor:not-allowed}' +
      '.ab [data-more].abf-nomore,.ab [data-more][hidden]{display:none}';
    document.head.appendChild(s);
  }

  var qty = function (card) { return Number(card.dataset.qty) || 0; };
  var maxOf = function (card) {
    if (!card || card.dataset.max === undefined || card.dataset.max === '') return Infinity;
    var m = Number(card.dataset.max);
    return isFinite(m) ? Math.max(0, m) : Infinity;
  };
  var visible = function (el) { return !!el && el.getClientRects().length > 0; };

  /* ---------- 1. stock limits ---------- */
  function paintStock(card) {
    var max = maxOf(card);
    if (max === Infinity) return;
    var atMax = qty(card) >= max;
    var plus = card.querySelector('[data-plus]');
    if (plus) {
      plus.setAttribute('aria-disabled', String(atMax));
      plus.setAttribute('aria-label', atMax ? 'No more in stock' : 'Add one');
    }
    var note = card.querySelector('.abf-stock');
    if (atMax && qty(card) > 0) {
      if (!note) {
        note = document.createElement('span');
        note.className = 'abf-stock';
        note.setAttribute('role', 'status');
        var actions = card.querySelector('.ab-card__actions') || card;
        actions.appendChild(note);
      }
      var text = 'Only ' + max + ' in stock';
      if (note.textContent !== text) note.textContent = text;
    } else if (note) {
      note.remove();
    }
  }

  function stockGuard(event) {
    var btn = event.target.closest && event.target.closest('[data-plus],[data-add],[data-toggle]');
    if (!btn) return;
    var card = btn.closest('.ab-card');
    if (!card || !card.closest('.ab')) return;
    var max = maxOf(card);
    if (max === Infinity) return;
    var n = qty(card);
    /* + always adds; Add and the photo add only when nothing is picked yet (otherwise they remove). */
    var adding = btn.hasAttribute('data-plus') || n === 0;
    if (adding && n >= max) {
      event.preventDefault();
      event.stopImmediatePropagation();
      paintStock(card);
      var note = card.querySelector('.abf-stock');
      if (!note && max === 0) {
        note = document.createElement('span');
        note.className = 'abf-stock';
        note.setAttribute('role', 'status');
        (card.querySelector('.ab-card__actions') || card).appendChild(note);
        note.textContent = 'Out of stock';
      }
    }
  }

  /* ---------- 2 and 3. show more / show fewer ---------- */
  function hiddenByCollapse(grid) {
    return Array.prototype.filter.call(grid.querySelectorAll('.ab-card'), function (c) {
      return !c.hidden && !visible(c);
    });
  }

  function labelMore(more, left) {
    more.textContent = left > BATCH ? 'Show ' + BATCH + ' more (' + left + ' left)' : 'Show the last ' + left;
    more.setAttribute('aria-expanded', 'false');
  }

  function moreClick(event) {
    var more = event.target.closest && event.target.closest('.ab [data-more]');
    if (!more) return;
    var step = more.closest('.ab-step') || more.parentElement;
    var grid = step && step.querySelector('[data-grid], .ab-grid');
    if (!grid) return;
    var wasOpen = grid.classList.contains('is-expanded');

    if (!wasOpen) {
      var waiting = hiddenByCollapse(grid);
      if (waiting.length > BATCH) {
        /* Open the next batch ourselves and keep the list collapsed. */
        event.preventDefault();
        event.stopImmediatePropagation();
        waiting.slice(0, BATCH).forEach(function (c) { c.classList.add('abf-shown'); });
        labelMore(more, waiting.length - BATCH);
        return;
      }
      /* Last batch: let the builder expand the grid, then tidy up the label. */
      window.setTimeout(function () {
        more.setAttribute('aria-expanded', String(grid.classList.contains('is-expanded')));
      }, 0);
      return;
    }

    /* Collapsing: the builder toggles the class, then we clear our batches and scroll back to the step. */
    window.setTimeout(function () {
      if (grid.classList.contains('is-expanded')) return;
      Array.prototype.forEach.call(grid.querySelectorAll('.abf-shown'), function (c) { c.classList.remove('abf-shown'); });
      more.setAttribute('aria-expanded', 'false');
      var top = step.getBoundingClientRect().top;
      if (top < 0 || top > window.innerHeight) {
        var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        step.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
      }
    }, 0);
  }

  /* A filter opens or closes every grid, so start the batches again from the top. */
  function filterClick(event) {
    var btn = event.target.closest && event.target.closest('.ab [data-filter-btn]');
    if (!btn) return;
    var root = btn.closest('.ab');
    Array.prototype.forEach.call(root.querySelectorAll('.abf-shown'), function (c) { c.classList.remove('abf-shown'); });
    window.setTimeout(function () { setAria(root); }, 0);
  }

  function setAria(root) {
    Array.prototype.forEach.call(root.querySelectorAll('[data-more]'), function (more) {
      var step = more.closest('.ab-step') || more.parentElement;
      var grid = step && step.querySelector('[data-grid], .ab-grid');
      more.setAttribute('aria-expanded', String(!!grid && grid.classList.contains('is-expanded') && !more.hidden));
    });
  }

  /* ---------- 7. no pointless "Show all" ---------- */
  /* The builders add "Show all" to any step with more than 8 items, but wide screens show up to 12, so on desktop a step
     with 9 to 12 items had a button that showed nothing new. Hide it while nothing in that step is actually hidden.
     Only steps on screen are measured; a step is re-checked when it opens, a filter changes or the window resizes. */
  function syncMore(root) {
    Array.prototype.forEach.call(root.querySelectorAll('[data-more]'), function (more) {
      var step = more.closest('.ab-step') || more.parentElement;
      var grid = step && step.querySelector('[data-grid], .ab-grid');
      if (!grid || !visible(grid)) return;
      var collapsed = !grid.classList.contains('is-expanded');
      var nothingHidden = collapsed && !grid.querySelector('.abf-shown') && hiddenByCollapse(grid).length === 0;
      more.classList.toggle('abf-nomore', nothingHidden);
    });
  }

  /* ---------- 4. Christmas free delivery line ---------- */
  function freeDelivery(root, cartTotal) {
    var hint = root.querySelector('[data-bundle-hint]');
    if (!hint || !(cartTotal > 0)) return;
    var written = '';
    var re = /\. Add £([\d,]+(?:\.\d+)?) more for free UK mainland delivery/;
    var fix = function () {
      var t = hint.textContent;
      if (t === written) return;
      var m = t.match(re);
      if (!m) return;
      var left = Number(m[1].replace(/,/g, '')) - cartTotal;
      var next = left <= 0
        ? t.replace(re, ', with free UK mainland delivery')
        : t.replace(re, '. Add ' + money.format(left) + ' more for free UK mainland delivery');
      written = next;
      if (next !== t) hint.textContent = next;
    };
    new MutationObserver(fix).observe(hint, { childList: true, characterData: true, subtree: true });
    fix();
  }

  /* ---------- 5. Gift box builders: "Put this in a gift box" link from a product page ---------- */
  /* The builder (sections/auro-bundle-wizard.liquid) adds the linked item, then opens the item's own step, often the
     last one ("Finishing touches"), so shoppers skipped light, scent and keepsake. Once it has done that, walk back to
     the first step with the builder's own Back button, the same as a shopper tapping it, and say the item is in. */
  var FIRST = 'light';
  var LATER = ['scent', 'keepsake', 'extra'];
  function giftLink() {
    var param = '';
    try { param = new URLSearchParams(window.location.search).get('add') || ''; } catch (e) { param = ''; }
    var root = document.querySelector('[data-gift-builder]');
    if (!param || !root) return;
    /* Hide the step panel while walking back, so shoppers don't see it flick through the steps. */
    var s = document.createElement('style');
    s.textContent = '[data-gift-builder].abf-linking .abw-gstep{visibility:hidden}';
    document.head.appendChild(s);
    var nameP = /^\d+$/.test(param)
      ? Promise.resolve('')
      : fetch((window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/') + 'products/' + encodeURIComponent(param) + '.js', { headers: { Accept: 'application/json' } })
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (p) { return p && p.title ? String(p.title) : ''; })
          .catch(function () { return ''; });
    var started = Date.now();
    var at = function () { return root.getAttribute('data-abw-at') || ''; };
    var back = function () { return root.querySelector('[data-abw-back]'); };
    /* The builder removes ?add= from the address once it has placed the item (or given up). */
    var placed = function () {
      try { return !new URLSearchParams(window.location.search).has('add'); } catch (e) { return true; }
    };
    var note = function () {
      nameP.then(function (name) {
        var landed = at();
        if (landed === 'start' || landed === 'review' || root.dataset.wiz !== 'on') return;
        var guide = root.querySelector('.abw-progress__guide');
        if (!guide) return;
        var p = document.createElement('p');
        p.className = 'abw-note abf-linknote';
        p.setAttribute('role', 'status');
        p.textContent = (name ? name + ' is' : 'Your item is') + ' in your gift. Add the rest step by step, or skip ahead.';
        p.style.marginTop = '1.2rem';
        guide.insertAdjacentElement('afterend', p);
        /* Gone as soon as the shopper moves on. */
        var mo = new MutationObserver(function () {
          if (at() !== landed) { p.remove(); mo.disconnect(); }
        });
        mo.observe(root, { attributes: true, attributeFilter: ['data-abw-at'] });
      });
    };
    var done = function () {
      root.classList.remove('abf-linking');
      note();
    };
    var walk = function (left) {
      var now = at();
      if (now === FIRST || left <= 0) { done(); return; }
      if (now === 'start') {
        /* The first step had nothing to show and Back skipped past it: go forward to the first step that has items. */
        var own = root.querySelector('[data-abw-own]');
        if (own) own.click();
        window.setTimeout(done, 150);
        return;
      }
      var b = back();
      if (!b || b.hidden) { done(); return; }
      b.click();
      window.setTimeout(function () { walk(left - 1); }, 150);
    };
    var wait = function () {
      if (Date.now() - started > 20000) return;
      if (root.dataset.wiz === 'on' && placed()) {
        var now = at();
        if (LATER.indexOf(now) > -1) {
          root.classList.add('abf-linking');
          window.setTimeout(function () { root.classList.remove('abf-linking'); }, 5000);
          walk(4);
        } else if (now === FIRST) {
          note();
        }
        return;
      }
      window.setTimeout(wait, 120);
    };
    wait();
  }

  /* ---------- 6. Gift box builders: keep picks in view ---------- */
  /* The builder shows 24 items per step, and "Show more" adds 24 at a time. A filter chip or search resets it to 24,
     so an item picked from further down drops out of view when the filter is cleared (it stays in the gift).
     When that happens, tap "Show more" again, but only as far as the shopper had already opened that step. */
  var FILL_STEPS = ['light', 'scent', 'keepsake', 'extra'];
  function giftKeepPicks() {
    var root = document.querySelector('[data-gift-builder]');
    if (!root) return;
    var deepest = {};
    var timer = null;
    var filtered = function () {
      var chip = root.querySelector('.abw-chip[aria-pressed="true"]');
      var search = root.querySelector('.abw-search');
      return Boolean((chip && chip.getAttribute('data-abw-type')) || (search && search.value.trim()));
    };
    var check = function () {
      if (root.dataset.wiz !== 'on') return;
      var at = root.getAttribute('data-abw-at') || '';
      if (FILL_STEPS.indexOf(at) < 0 || filtered()) return;
      var panel = root.querySelector('.abw-gstep');
      if (!panel) return;
      var cards = panel.querySelectorAll('[data-abw-card]');
      var hint = root.querySelector('.abw-hint');
      var m = hint && hint.textContent.match(/^(\d+) added from this step/);
      var picked = m ? Number(m[1]) : 0;
      var inView = 0;
      Array.prototype.forEach.call(cards, function (c) {
        if (!c.classList.contains('is-selected')) return;
        var n = c.querySelector('.gb-stepper [role="status"]');
        inView += n ? (Number(n.textContent) || 1) : 1;
      });
      var more = panel.querySelector('[data-abw-more]');
      if (picked > inView && more && cards.length < (deepest[at] || 0)) {
        more.click();
        return;
      }
      deepest[at] = Math.max(deepest[at] || 0, cards.length);
    };
    new MutationObserver(function () {
      window.clearTimeout(timer);
      timer = window.setTimeout(check, 30);
    }).observe(root, { childList: true, subtree: true });
  }

  /* ---------- start ---------- */
  function init() {
    giftLink();
    giftKeepPicks();
    var roots = document.querySelectorAll('.ab');
    if (!roots.length) return;
    css();
    document.addEventListener('click', stockGuard, true);
    document.addEventListener('click', moreClick, true);
    document.addEventListener('click', filterClick, true);

    /* Some builders (Meaning) create their Show all buttons when the page finishes loading, after this runs. */
    var ariaAll = function () { Array.prototype.forEach.call(roots, function (root) { setAria(root); syncMore(root); }); };
    ariaAll();
    if (document.readyState === 'loading' || document.readyState === 'interactive') document.addEventListener('DOMContentLoaded', ariaAll);
    window.addEventListener('load', ariaAll);
    /* Re-check "Show all" when a step opens, a filter changes or cards are picked, and when the window is resized. */
    var syncTimer = null;
    var syncSoon = function () {
      window.clearTimeout(syncTimer);
      syncTimer = window.setTimeout(function () { Array.prototype.forEach.call(roots, syncMore); }, 60);
    };
    window.addEventListener('resize', syncSoon);
    Array.prototype.forEach.call(roots, function (root) {
      new MutationObserver(function (records) {
        /* Ignore our own button class change so this does not loop. */
        if (records.every(function (r) { return r.target.hasAttribute && r.target.hasAttribute('data-more'); })) return;
        syncSoon();
      }).observe(root, { subtree: true, attributes: true, attributeFilter: ['class', 'hidden'] });
    });

    Array.prototype.forEach.call(roots, function (root) {
      var cards = root.querySelectorAll('.ab-card[data-max]');
      Array.prototype.forEach.call(cards, paintStock);
      new MutationObserver(function (records) {
        records.forEach(function (r) {
          if (r.attributeName === 'data-qty' && r.target.matches('.ab-card[data-max]')) paintStock(r.target);
        });
      }).observe(root, { subtree: true, attributes: true, attributeFilter: ['data-qty'] });
    });

    var xmas = document.querySelectorAll('[data-christmas-bundle]');
    if (xmas.length) {
      fetch((window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/') + 'cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (cart) {
          if (!cart) return;
          var total = (Number(cart.total_price) || 0) / 100;
          Array.prototype.forEach.call(xmas, function (root) { freeDelivery(root, total); });
        })
        .catch(function () { /* leave the line as the builder wrote it */ });
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
